// ── Inbox — Reactions + emoji picker popover ─────────────────
//
// Owns the reaction-toggle network call (with optimistic update), the
// floating emoji-picker popover lifecycle, and applying the load-once
// "ranking" (messages/emoji-ranking.js) that personalizes the coach's
// top-5 quick-react emojis.
//
// State owned by the factory closure:
//   - `_emojiPopoverEl` — current popover DOM node (or null)
//   - `_emojiPopoverTargetMsgId` — which message the popover targets
//
// State passed in via callbacks:
//   - `_activeMessages` (read for optimistic toggle target)
//   - `_quickEmojis` (mutated via setQuickEmojis after ranking fetch)
//
// `toggleReact` and `openEmojiPicker` are returned to the parent, whose
// thread listener calls them for the reaction and "+" buttons.
// `loadEmojiRanking` is also exposed so the parent can fire it once on
// inbox init.

import { getSupabase } from '../config.js';
import { EMOJI_CATS } from './format-helpers.js';
import { loadEmojiRanking as loadSharedEmojiRanking } from '../messages/emoji-ranking.js';

/**
 * @param {object} deps
 * @param {() => any[]} deps.getActiveMessages
 * @param {(arr: string[]) => void} deps.setQuickEmojis
 * @param {() => void} deps.renderChatMessages
 */
export function createInboxEmoji(deps) {
  const { getActiveMessages, setQuickEmojis, renderChatMessages } = deps;

  let _emojiPopoverEl = null;
  let _emojiPopoverTargetMsgId = null;

  async function toggleReact(msgId, encodedEmoji) {
    const emoji = decodeURIComponent(encodedEmoji);
    const msg = getActiveMessages().find(m => m.id === msgId);
    if (!msg) return;

    // Optimistic update
    const existing = (msg.reactions || []).find(r => r.emoji === emoji);
    if (!msg.reactions) msg.reactions = [];
    if (existing) {
      if (existing.mine) {
        existing.mine = false;
        existing.count = Math.max(0, existing.count - 1);
      } else {
        existing.mine = true;
        existing.count += 1;
      }
      msg.reactions = msg.reactions.filter(r => r.count > 0);
    } else {
      msg.reactions.push({ emoji, count: 1, mine: true });
    }
    renderChatMessages();

    try {
      const supabase = getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/.netlify/functions/messages-react', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ messageId: msgId, emoji }),
      });
      const json = await res.json();
      if (res.ok && json.reactions) {
        msg.reactions = json.reactions;
        renderChatMessages();
      }
    } catch {}
  }

  // anchorEl: the button pressed (the thread's one listener passes it; the
  // event's currentTarget is then the thread, not the button).
  function openEmojiPicker(event, msgId, anchorEl) {
    event?.stopPropagation();
    closeEmojiPicker();
    _emojiPopoverTargetMsgId = msgId;

    const anchor = anchorEl || event?.currentTarget || event?.target;
    const rect = anchor?.getBoundingClientRect?.() || { top: 100, left: 100, bottom: 120 };

    const popover = document.createElement('div');
    popover.className = 'inbox-emoji-popover';
    popover.id = 'inbox-emoji-popover';
    _emojiPopoverEl = popover;

    // Position: prefer above the anchor if there's room, otherwise below
    const popoverHeight = 380;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let top, left;
    const aboveRoom = rect.top;
    if (aboveRoom > popoverHeight + 20) {
      top = rect.top - popoverHeight - 8;
    } else {
      top = Math.min(rect.bottom + 8, vh - popoverHeight - 12);
    }
    left = Math.min(Math.max(12, rect.left - 100), vw - 332);

    popover.style.top = `${top + window.scrollY}px`;
    popover.style.left = `${left}px`;

    let activeCat = 'smileys';

    const render = () => {
      const cat = EMOJI_CATS.find(c => c.key === activeCat) || EMOJI_CATS[0];
      popover.innerHTML = `
        <div class="inbox-emoji-tabs">
          ${EMOJI_CATS.map(c => `<button class="inbox-emoji-tab${c.key === activeCat ? ' active' : ''}" data-cat="${c.key}">${c.label}</button>`).join('')}
        </div>
        <div class="inbox-emoji-grid">
          ${cat.emojis.map(e => `<button class="inbox-emoji-cell" data-emoji="${encodeURIComponent(e)}">${e}</button>`).join('')}
        </div>
      `;
      popover.querySelectorAll('.inbox-emoji-tab').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          activeCat = btn.dataset.cat;
          render();
        });
      });
      popover.querySelectorAll('.inbox-emoji-cell').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const emoji = btn.dataset.emoji;
          if (emoji && _emojiPopoverTargetMsgId) {
            toggleReact(_emojiPopoverTargetMsgId, emoji);
          }
          closeEmojiPicker();
        });
      });
    };
    render();
    document.body.appendChild(popover);

    // Close on outside click (delayed so the opening click doesn't dismiss)
    setTimeout(() => {
      document.addEventListener('click', closeEmojiPickerOnOutside, { once: true });
    }, 10);
  }

  function closeEmojiPicker() {
    if (_emojiPopoverEl) {
      _emojiPopoverEl.remove();
      _emojiPopoverEl = null;
    }
    _emojiPopoverTargetMsgId = null;
  }

  function closeEmojiPickerOnOutside(e) {
    if (_emojiPopoverEl && !_emojiPopoverEl.contains(e.target)) {
      closeEmojiPicker();
    } else if (_emojiPopoverEl) {
      // Clicked inside — re-arm
      document.addEventListener('click', closeEmojiPickerOnOutside, { once: true });
    }
  }

  // Load the coach's top-used emoji ranking (ranked by their own reaction
  // history; padded with defaults). Fire-and-forget — the fallback defaults
  // are already in place, so any failure is silent. The request is the shared
  // one (messages/emoji-ranking.js): once per session, and a failure lets the
  // next inbox open try again.
  async function loadEmojiRanking() {
    const emojis = await loadSharedEmojiRanking();
    if (Array.isArray(emojis) && emojis.length > 0) {
      // The shared answer carries 8 (the corner's "Most used"); the Inbox's
      // quick list stays the top 5.
      setQuickEmojis(emojis.slice(0, 5));
    }
  }

  return {
    toggleReact,
    openEmojiPicker,
    loadEmojiRanking,
  };
}
